import { Info } from "lucide-react";

/** Managed-configuration keys — Mobile/docs/phase0-contract.md §6. */
const KEYS: Array<{ key: string; type: string; effect: string }> = [
    { key: "serverUrl", type: "string", effect: "Where the app finds your Athavita deployment (default https://login.athavita.com)." },
    { key: "tenantId", type: "string", effect: "Your tenant id. Signing in to any other tenant is refused." },
    { key: "allowedAccounts", type: "string", effect: "Comma-separated emails or @domain entries allowed to sign in (Allowed accounts)." },
    { key: "requireAppPin", type: "bool", effect: "Require an app PIN." },
    { key: "blockScreenCapture", type: "bool", effect: "Block screenshots." },
    { key: "blockCopyOut", type: "bool", effect: "true = copying text out of the apps is blocked." },
    { key: "blockShareOut", type: "bool", effect: "true = sharing through the share sheet is blocked." },
    { key: "maxOfflineDays", type: "int", effect: "Maximum days offline." },
    { key: "allowAttachmentsToUnmanaged", type: "bool", effect: "Allow opening attachments in other apps." },
    { key: "attestationMode", type: "string", effect: "off, warn or block — device attestation." }
];

const APPS: Array<{ name: string; id: string }> = [
    { name: "Athavita Mail", id: "com.athavita.mail" },
    { name: "Athavita Connect", id: "com.athavita.connect" },
    { name: "Athavita Authenticator", id: "com.athavita.authenticator" }
];

export function AppConfigPanel() {
    return (
        <div className="max-w-4xl space-y-4">
            <section className="rounded-lg border bg-card p-4 shadow-sm">
                <h2 className="mb-2 flex items-center gap-2 font-semibold">
                    <Info className="h-4 w-4 text-primary" /> Managed configuration from your MDM
                </h2>
                <p className="text-sm text-muted-foreground">
                    Customers who already manage phones with an MDM (Microsoft Intune, Jamf, Workspace ONE, Google
                    endpoint management and so on) can push settings straight to the Athavita apps — &ldquo;App
                    Config&rdquo; on iOS, &ldquo;managed configurations&rdquo; on Android. The keys are the same in all
                    three apps. Nothing needs to be set up in this console for it to work.
                </p>
                <p className="mt-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
                    <strong>Most restrictive wins.</strong> The app combines the MDM settings with the app protection
                    policy from this console and always keeps the stricter of the two, so an MDM can tighten your
                    policy but never loosen it.
                </p>
                <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                    <li>Block / require switches are on if either side turns them on; &ldquo;allow&rdquo; switches need both.</li>
                    <li>PIN length: the longer one. Retry limits, lock timeouts and offline limits: the shorter one.</li>
                    <li>Copy, share, attestation and pinning choices: the stricter option. Minimum versions: the higher one.</li>
                    <li>Allowed accounts: only accounts on both lists (when both set one).</li>
                    <li>The network allowlist is set here only — the MDM can&apos;t change it.</li>
                </ul>
            </section>

            <section className="rounded-lg border bg-card shadow-sm">
                <header className="border-b px-4 py-3 text-sm font-semibold">Configuration keys</header>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b bg-muted/40 text-left text-xs font-medium text-muted-foreground">
                            <tr>
                                <th scope="col" className="px-4 py-2">Key</th>
                                <th scope="col" className="px-4 py-2">Type</th>
                                <th scope="col" className="px-4 py-2">Effect</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {KEYS.map((k) => (
                                <tr key={k.key}>
                                    <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">{k.key}</td>
                                    <td className="px-4 py-2 text-xs text-muted-foreground">{k.type}</td>
                                    <td className="px-4 py-2 text-xs">{k.effect}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="rounded-lg border bg-card shadow-sm">
                <header className="border-b px-4 py-3 text-sm font-semibold">App identifiers for MDM assignment</header>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b bg-muted/40 text-left text-xs font-medium text-muted-foreground">
                            <tr>
                                <th scope="col" className="px-4 py-2">App</th>
                                <th scope="col" className="px-4 py-2">iOS bundle id / Android package</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {APPS.map((a) => (
                                <tr key={a.id}>
                                    <td className="px-4 py-2">{a.name}</td>
                                    <td className="px-4 py-2 font-mono text-xs">{a.id}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
